import type { FC } from 'react';
import { Controller } from 'react-hook-form';
import type { RadioCardOption } from '@components/RadioCardGroup';
import { RadioCardGroup } from '@components/RadioCardGroup';
import type { EntityData } from '@ContractBuilder/types';

import type { StepProps } from '../types';
import { isTemplateResource } from '../utils/isTemplateResource';

type RenewalType = 'refresh' | 'replace' | 'replicate';

const getTooltipContent = (submission?: EntityData) => {
  if (isTemplateResource(submission?.sourceResourceName)) {
    return 'Current template is not published';
  }

  return 'Original contract is based on a contract';
};

export const Step2: FC<StepProps> = ({ control, canRefreshCurrentTemplate, submission }) => {
  const options: RadioCardOption<RenewalType>[] = [
    {
      value: 'refresh',
      title: 'Refresh current template',
      description: 'Update the current template, refreshing all blocks',
      dataTestId: 'renewal-type-refresh',
      isDisabled: !canRefreshCurrentTemplate,
      tooltipContent: getTooltipContent(submission),
    },
    {
      value: 'replace',
      title: 'Replace with a new template',
      description: 'Swap out the template, replacing all blocks',
      dataTestId: 'renewal-type-replace',
    },
    {
      value: 'replicate',
      title: 'Replicate contract',
      description: 'Copy the original contract, no template updates',
      dataTestId: 'renewal-type-replicate',
    },
  ];

  return (
    <Controller
      control={control}
      name="type"
      render={({ field }) => (
        <RadioCardGroup
          labelText="Select renewal type"
          options={options}
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
        />
      )}
    />
  );
};
